import React, { useCallback, useEffect, useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import propertiesService from "../../services/propertiesService";
import DateTimeModal from "../DateTimeModal";
import userRoleUtils from "../../utils/userRoleUtils";
import DefaultOrder from "../DefaultOrder"; // Adjust path as needed
import SchedoxSyncStatus from "../sync/SchedoxSyncStatus";
import PropertyRecurrencePanel from "./PropertyRecurrencePanel";
import { Modal } from "antd";

const EditPropertyForm = ({ propertyId, onSave, onCancel }) => {
  const canManageSchedoxSync = userRoleUtils.canManageSchedoxSync();
  const canManageRecurrence = userRoleUtils.canManagePropertyRecurrence();
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [resolvedPropertyId, setResolvedPropertyId] = useState(null);
  const [userSyncEnabled, setUserSyncEnabled] = useState(null);
  const loadSchedoxSync = useCallback(() => propertiesService.getSchedoxSyncStatus(resolvedPropertyId), [resolvedPropertyId]);
  const startSchedoxSync = useCallback(() => propertiesService.startSchedoxSync(resolvedPropertyId), [resolvedPropertyId]);
  const handleSchedoxStatus = useCallback((status) => {
    setUserSyncEnabled(Boolean(status?.sources?.user_toggle));
  }, []);
  const [formData, setFormData] = useState({
    property_id: "",
    userid: "",
    property_title: "",
    property_type: "",
    property_description: "",
    contact_name: "",
    cleaner_information: "",
    contact_number: "",
    contact_description: "",
    arrival_times: ["08", "00"],
    completion_times: ["09", "30"],
    date: "",
    quantity: {},
    appartment_address: "",
    appartment_number: "",
    delivery_city: "",
    delivery_country: "",
    post_code: "",
    product_quantity: {},
    sync_to_schedox: false,
    schedox_service_id: "",
  });

  const [propertyTypes, setPropertyTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [packs, setPacks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [schedoxServices, setSchedoxServices] = useState([]);
  const [isDateTimeModalOpen, setIsDateTimeModalOpen] = useState(false);


  useEffect(() => {
    setResolvedPropertyId(null);
    setUserSyncEnabled(null);
    fetchPropertyData();
  }, [propertyId]);

  const getMinimumDate = () => {
    return userRoleUtils.getMinimumOrderDate();
  };

  const handleDateTimeModalSave = (updatedDateTime) => {
    // Extract hours and minutes from arrival_time and completion_time
    let arrivalTimes = ['08', '00'];
    if (updatedDateTime.arrival_time && updatedDateTime.arrival_time.includes(':')) {
      arrivalTimes = updatedDateTime.arrival_time.split(':');
    }
    
    let completionTimes = ['09', '30'];
    if (updatedDateTime.completion_time && updatedDateTime.completion_time.includes(':')) {
      completionTimes = updatedDateTime.completion_time.split(':');
    }

    const minDate = new Date(getMinimumDate());
    
    setFormData(prev => ({
      ...prev,
      arrival_times: arrivalTimes,
      completion_times: completionTimes,
      date: updatedDateTime.order_date || minDate.toISOString().split('T')[0]
    }));
    
    setIsDateTimeModalOpen(false);
  };

  const fetchPropertyData = async () => {
    try {
      const response = await propertiesService.getPropertyById(propertyId);
      if (response && response.property) {
        const property = response.property;
        setResolvedPropertyId(property.id);

        // Safely parse arrival time and completion time
        let arrivalTimes = ["08", "00"];
        if (property.arrival_time && property.arrival_time.includes(":")) {
          arrivalTimes = property.arrival_time.split(":");
        }
        
        let completionTimes = ["09", "30"];
        if (property.completion_time && property.completion_time.includes(":")) {
          completionTimes = property.completion_time.split(":");
        }

        // Initialize date to minimum date if not provided
        const currentDate = new Date();
        const defaultDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        
        // Ensure we have valid values for quantity and product_quantity
        let quantity = {};
        try {
          quantity = property.quantity ? JSON.parse(property.quantity) : {};
        } catch (e) {
          console.error("Error parsing quantity:", e);
        }
        
        let productQuantity = {};
        try {
          productQuantity = property.product_quantity ? JSON.parse(property.product_quantity) : {};
        } catch (e) {
          console.error("Error parsing product_quantity:", e);
        }

        setFormData({
          userid: property.user_id || "",
          property_id: property.id ? property.id.toString() : "",
          property_title: property.property_title || "",
          property_type: property.property_type_id ? property.property_type_id.toString() : "",
          property_description: property.property_description || "",
          contact_name: property.contact_person || "",
          cleaner_information: property.cleaner_information || "",
          contact_number: property.contact_number || "",
          contact_description: property.contact_description || "",
          arrival_times: arrivalTimes,
          completion_times: completionTimes,
          date: property.date || defaultDate,
          quantity: quantity,
          appartment_address: property.apartment_address || "",
          appartment_number: property.apartment_number || "",
          delivery_city: property.delivery_city || "",
          delivery_country: property.delivery_country || "",
          post_code: property.delivery_zip_code || "",
          product_quantity: productQuantity,
          sync_to_schedox: Boolean(property.sync_to_cleaning),
          schedox_service_id: property.schedox_service_id || "",
        });

        // Set other state variables if data is available
        if (response.propertytype) setPropertyTypes(response.propertytype);
        if (response.countries) setCountries(response.countries);
        if (response.categories) setCategories(response.categories);
        if (response.products) setProducts(response.products || {});
        if (response.packs) setPacks(response.packs || []);
        if (response.rooms) setRooms(response.rooms || []);
        if (response.schedox_services) setSchedoxServices(response.schedox_services || []);
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "arrival_time" || name === "completion_time") {
      const [hours, minutes] = value.split(":");
      setFormData((prevData) => ({
        ...prevData,
        [`${name}s`]: [hours, minutes],
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleRoomChange = (roomId, value) => {
    setFormData((prevData) => ({
      ...prevData,
      quantity: { ...prevData.quantity, [roomId]: parseInt(value) },
    }));
  };

  const handleQuantityChange = (productId, change, isProduct, price) => {
    setFormData((prevData) => {
      const currentQuantity = prevData.product_quantity[productId]?.qty || 0;
      // Convert to number and ensure we're working with floats
      const numCurrentQuantity = parseFloat(currentQuantity);
      const numChange = parseFloat(change);
      // Calculate new quantity with precision handling
      const newQuantity = Math.max(0, numCurrentQuantity + numChange);
      // Round to 2 decimal places
      const roundedQuantity = Math.round(newQuantity * 100) / 100;

      if (roundedQuantity === 0) {
        const { [productId]: _, ...rest } = prevData.product_quantity;
        return { ...prevData, product_quantity: rest };
      }

      return {
        ...prevData,
        product_quantity: {
          ...prevData.product_quantity,
          [productId]: {
            id: productId,
            price: price,
            type: isProduct ? "product" : "pack",
            qty: roundedQuantity,
          },
        },
      };
    });
  };

  const handleRentTypeChange = (productId, rentType) => {
    setFormData((prevData) => ({
      ...prevData,
      product_quantity: {
        ...prevData.product_quantity,
        [productId]: { ...prevData.product_quantity[productId], rentType },
      },
    }));
  };

  const handleSave = async () => {
    try {
      const dataToSend = new FormData();
      dataToSend.append("type", 1);
      // Add _token (you might need to get this from somewhere)
      //if user is admin add userid from formData->user_id

      dataToSend.append("userid", formData.userid);
      // Add basic fields
      Object.entries(formData).forEach(([key, value]) => {
        if (!canManageSchedoxSync && ['sync_to_schedox', 'schedox_service_id'].includes(key)) return;
        if (
          ![
            "quantity",
            "product_quantity",
            "arrival_times",
            "completion_times",
          ].includes(key)
        ) {
          dataToSend.append(key, value);
        }
      });

      // Add arrival and completion times
      formData.arrival_times.forEach((time) =>
        dataToSend.append("arrival_times[]", time)
      );
      formData.completion_times.forEach((time) =>
        dataToSend.append("completion_times[]", time)
      );

      // Add quantity
      Object.entries(formData.quantity).forEach(([roomId, count]) => {
        dataToSend.append(`quantity[${roomId}]`, count);
      });

      // Add product_quantity
      Object.entries(formData.product_quantity).forEach(
        ([productId, product]) => {
          Object.entries(product).forEach(([field, value]) => {
            dataToSend.append(
              `product_quantity[${productId}][${field}]`,
              value
            );
          });
        }
      );

      //if user is admin add userid from property user
      if (userRoleUtils.isAdmin()) {
        const user = JSON.parse(localStorage.getItem("user"));
        dataToSend.append("userid", user.id);
      }

      const response = await propertiesService.updateProperty(dataToSend);
      if (response.status) {
        onSave(response);
      } else {
        console.error("Failed to update property:", response.message);
      }
    } catch (error) {
      console.error("Error updating property:", error);
    }
  };

  const handleSaveAndAddToCart = async () => {
    try {
      const dataToSend = new FormData();
      dataToSend.append("type", 2);
      // Add _token (you might need to get this from somewhere)

      // Add basic fields
      Object.entries(formData).forEach(([key, value]) => {
        if (
          ![
            "quantity",
            "product_quantity",
            "arrival_times",
            "completion_times",
          ].includes(key)
        ) {
          dataToSend.append(key, value);
        }
      });

      // Add arrival and completion times
      formData.arrival_times.forEach((time) =>
        dataToSend.append("arrival_times[]", time)
      );
      formData.completion_times.forEach((time) =>
        dataToSend.append("completion_times[]", time)
      );

      // Add quantity
      Object.entries(formData.quantity).forEach(([roomId, count]) => {
        dataToSend.append(`quantity[${roomId}]`, count);
      });

      // Add product_quantity
      Object.entries(formData.product_quantity).forEach(
        ([productId, product]) => {
          Object.entries(product).forEach(([field, value]) => {
            dataToSend.append(
              `product_quantity[${productId}][${field}]`,
              value
            );
          });
        }
      );

      const response = await propertiesService.updateProperty(dataToSend);
      if (response.status) {
        onSave(response);
      } else {
        console.error("Failed to update property:", response.message);
      }
    } catch (error) {
      console.error("Error updating property:", error);
    }
  };

  const handleAddToCartOnly = async () => {
    try {
      const dataToSend = new FormData();
      dataToSend.append("type", 3);
      // Add _token (you might need to get this from somewhere)

      // Add basic fields
      Object.entries(formData).forEach(([key, value]) => {
        if (
          ![
            "quantity",
            "product_quantity",
            "arrival_times",
            "completion_times",
          ].includes(key)
        ) {
          dataToSend.append(key, value);
        }
      });

      // Add arrival and completion times
      formData.arrival_times.forEach((time) =>
        dataToSend.append("arrival_times[]", time)
      );
      formData.completion_times.forEach((time) =>
        dataToSend.append("completion_times[]", time)
      );

      // Add quantity
      Object.entries(formData.quantity).forEach(([roomId, count]) => {
        dataToSend.append(`quantity[${roomId}]`, count);
      });

      // Add product_quantity
      Object.entries(formData.product_quantity).forEach(
        ([productId, product]) => {
          Object.entries(product).forEach(([field, value]) => {
            dataToSend.append(
              `product_quantity[${productId}][${field}]`,
              value
            );
          });
        }
      );

      const response = await propertiesService.updateProperty(dataToSend);
      if (response.status) {
        onSave(response);
      } else {
        console.error("Failed to update property:", response.message);
      }
    } catch (error) {
      console.error("Error updating property:", error);
    }
  };
  const handleAddExtraItem = (extraItem) => {
    setFormData((prevData) => ({
      ...prevData,
      product_quantity: {
        ...prevData.product_quantity,
        [extraItem.id]: {
          id: extraItem.id,
          name: extraItem.name,
          qty: extraItem.qty,
          price: extraItem.price,
          isExtra: true,
          type: "extra",
        },
      },
    }));
  };

  const handleRemoveExtraItem = (extraItemId) => {
    setFormData((prevData) => {
      const nextProductQuantity = { ...prevData.product_quantity };
      delete nextProductQuantity[extraItemId];
      return { ...prevData, product_quantity: nextProductQuantity };
    });
  };

  const handleDiscountChange = ({ enabled, type, value }) => {
    setFormData((prevData) => {
      const nextItems = { ...prevData.product_quantity };
      if (!enabled || Number(value) <= 0) {
        delete nextItems["discount-default"];
      } else {
        nextItems["discount-default"] = {
          id: "discount-default",
          name: "Default order discount",
          qty: 1,
          price: 0,
          type: "discount",
          discount_type: type,
          discount_value: Number(value),
        };
      }
      return { ...prevData, product_quantity: nextItems };
    });
  };
  
  useEffect(() => {
    // Check if the current date is less than minDate
    const currentDate = new Date(formData.date);
    if (currentDate < getMinimumDate()) {
      // Update the date to minDate if it's less than minimum
      handleInputChange({
        target: {
          name: 'date',
          value: getMinimumDate().toISOString().split('T')[0]
        }
      });
    }
  }, [formData.date]);
  
  // Format times for display
  const formattedArrivalTime = formData.arrival_times && formData.arrival_times.length === 2 
    ? `${formData.arrival_times[0].padStart(2, '0')}:${formData.arrival_times[1].padStart(2, '0')}`
    : "08:00";
    
  const formattedCompletionTime = formData.completion_times && formData.completion_times.length === 2
    ? `${formData.completion_times[0].padStart(2, '0')}:${formData.completion_times[1].padStart(2, '0')}`
    : "09:30";

  return (
    <div className="w-full min-w-0 border rounded-lg overflow-y-auto px-6 pt-6 pb-6">
      {canManageRecurrence && <div className="mb-6 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold">Recurring orders</h3>
          <p className="text-sm text-gray-600">Choose weekly dates for this property's saved default order.</p>
        </div>
        <button type="button" disabled={!resolvedPropertyId} onClick={() => setRecurrenceOpen(true)}
          className="shrink-0 rounded-lg bg-golden-navy px-4 py-2 text-white disabled:opacity-50">
          Set up recurring orders
        </button>
      </div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

      <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-xl font-semibold mb-4">About</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-sm text-gray-700">
                  Property ID*
                </label>
                <input
                  type="text"
                  name="property_title"
                  value={formData.property_title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Property Type*
                </label>
                <select
                  name="property_type"
                  value={formData.property_type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                >
                  <option value="">Select Type</option>
                  {propertyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.title}
                    </option>
                  ))}
                </select>
              </div>
              {canManageSchedoxSync && <>
              <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
                <input
                  type="checkbox"
                  name="sync_to_schedox"
                  checked={Boolean(formData.sync_to_schedox)}
                  onChange={handleInputChange}
                  disabled={!userSyncEnabled}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700">Sync to Schedox</span>
                  <span className="block text-xs text-gray-500">
                    {userSyncEnabled
                      ? 'This property can be switched off independently while its user remains enabled.'
                      : 'Enable Sync to Schedox on the user before enabling this property.'}
                  </span>
                </span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Schedox Service Type
                </label>
                <select
                  name="schedox_service_id"
                  value={formData.schedox_service_id}
                  onChange={handleInputChange}
                  disabled={!userSyncEnabled || !formData.sync_to_schedox}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue disabled:bg-gray-100"
                >
                  <option value="">Use Schedox default service</option>
                  {schedoxServices.map((service) => (
                    <option key={service.service_id} value={service.service_id}>
                      {service.name}{service.code ? ` (${service.code})` : ""}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-gray-500">
                  The selected Schedox service supplies the booking type and checklist; Golden order hours and price stay authoritative.
                </span>
              </div>
              {resolvedPropertyId && (
                <SchedoxSyncStatus
                  loadStatus={loadSchedoxSync}
                  startSync={startSchedoxSync}
                  onStatus={handleSchedoxStatus}
                />
              )}
              </>}
              <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                name="property_description"
                value={formData.property_description}
                onChange={handleInputChange}
                rows="3"
                className="mt-1 text-sm block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              ></textarea>
            </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-xl font-semibold mb-4">Contact</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact Name*
              </label>
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Access
                </label>
                <input
                  type="text"
                  name="cleaner_information"
                  value={formData.cleaner_information}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contact Number*
                </label>
                <input
                  type="text"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact Description
              </label>
              <textarea
                name="contact_description"
                value={formData.contact_description}
                onChange={handleInputChange}
                rows="3"
                className="mt-1 block text-sm w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              ></textarea>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-xl font-semibold mb-4">Rooms</h3>
          <div className="space-y-4">
            {Object.entries(formData.quantity).map(([room, count]) => (
              <div key={room} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {rooms.find((r) => r.id === parseInt(room))?.room_title || room}
                </span>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() =>
                      handleRoomChange(room, Math.max(0, count - 1))
                    }
                    className="bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center focus:outline-none"
                  >
                    -
                  </button>
                  <span className="mx-2 w-8 text-center">{count}</span>
                  <button
                    type="button"
                    onClick={() => handleRoomChange(room, count + 1)}
                    className="bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center focus:outline-none"
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

    

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">Default Timeslot</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <label className="block text-sm font-medium text-gray-700">Arrival Time*</label>
              <input
                type="time"
                readOnly
                value={formattedArrivalTime}
                className="mt-1 block w-full border border-gray-300 text-center rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                onClick={() => setIsDateTimeModalOpen(true)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Completion Time*</label>
              <input
                type="time"
                readOnly
                value={formattedCompletionTime}
                className="mt-1 block w-full border text-center border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                onClick={() => setIsDateTimeModalOpen(true)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              readOnly
              value={formData.date}
              className="mt-1 block w-full border text-center border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              onClick={() => setIsDateTimeModalOpen(true)}
            />
            <p className="text-xs text-gray-500 mt-1">Click to change date/time</p>
          </div>
        </div>
      </div>

        <div className="bg-white p-6 rounded-lg shadow col-span-2 border">
          <h3 className="text-xl font-semibold mb-4">Address</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Address*
              </label>
              <input
                type="text"
                name="appartment_address"
                value={formData.appartment_address}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Apartment/Building No*
              </label>
              <input
                type="text"
                name="appartment_number"
                value={formData.appartment_number}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
           
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">

<div>
  <label className="block text-sm font-medium text-gray-700">
    City*
  </label>
  <input
    type="text"
    name="delivery_city"
    value={formData.delivery_city}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  />
</div>
<div>
  <label className="block text-sm font-medium text-gray-700">
    Country*
  </label>
  <select
    name="delivery_country"
    value={formData.delivery_country}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  >
    <option value="">Select Country</option>
    {countries.map((country) => (
      <option key={country.id} value={country.sortname}>
        {country.name}
      </option>
    ))}
  </select>
</div>
<div>
  <label className="block text-sm font-medium text-gray-700">
    Post Code*
  </label>
  <input
    type="text"
    name="post_code"
    value={formData.post_code}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  />
</div>
</div>
        </div>
      </div>
      <DateTimeModal
        isOpen={isDateTimeModalOpen}
        onClose={() => setIsDateTimeModalOpen(false)}
        propertyId={propertyId}
        onSave={handleDateTimeModalSave}
        isSaveHidden={true}
        initialData={{
          arrival_time: formattedArrivalTime,
          completion_time: formattedCompletionTime,
          order_date: formData.date
        }}
      />
      <DefaultOrder
        categories={categories}
        products={products}
        packs={packs}
        onAddExtraItem={handleAddExtraItem}
        onRemoveExtraItem={handleRemoveExtraItem}
        onDiscountChange={handleDiscountChange}
        defaultOrder={formData.product_quantity}
        onQuantityChange={handleQuantityChange}
        onRentTypeChange={handleRentTypeChange}
      />

      {canManageRecurrence && <Modal title="Recurring property orders" open={recurrenceOpen}
        onCancel={() => setRecurrenceOpen(false)} footer={null} width={800} destroyOnClose>
        <PropertyRecurrencePanel propertyId={resolvedPropertyId} arrivalTime={formattedArrivalTime} completionTime={formattedCompletionTime} />
      </Modal>}

      <div className="mt-6 flex justify-end space-x-4">
       
        <button
          type="button"
          onClick={handleSave}
          className="bg-golden-navy text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors duration-300"
        >
          Update Property
        </button>

        <button
            type="button"
            onClick={handleSaveAndAddToCart}
            className="bg-golden-navy text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors duration-300"
            >
            Update Property and Add to Cart
        </button>

        <button
            type="button"
            onClick={handleAddToCartOnly}
            className="bg-white text-golden-navy px-6 py-2 rounded-lg border border-golden-navy hover:bg-golden-navy hover:text-white transition-colors duration-300"   
        >

            Add to Cart
        </button>
      </div>
    </div>
  );
};

export default EditPropertyForm;
